import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

import BaseDashboard from './BaseDashboard.tsx';
import DashboardHeader from './DashboardHeader.tsx';
import { nowISOString, subtractMinutes } from '../../utils/utils.ts';
import {
  getDashboardWidgets,
  createDashboardWidget,
  updateDashboardWidget,
  deleteDashboardWidget,
  reorderDashboardWidgets,
} from '../../services/api.ts';

import { WidgetDialog } from '../../components/WidgetDialog.tsx';
import { DashboardContent } from '../../components/DashboardContent.tsx';
import { WidgetInstance } from '../../components/charts/widgetRegistry.ts';

const EMPTY_WIDGET: Omit<WidgetInstance, 'id'> = {
  osm_name:       '',
  widget_type_id: 'sensor_gauge',
  chart_type:     'gauge',
  title:          null,
  colour:         null,
  min_value:      null,
  max_value:      null,
  layout_order:   0,
  measurement:    null,
  config:         null,
};

function toDbPayload(w: Partial<WidgetInstance>) {
  const { measurement, ...rest } = w as any;
  return rest;
}

type DynamicDashboardProps = {
  osmName:  string;
  isAdmin?: boolean;
};

export default function DynamicDashboard({ osmName, isAdmin = false }: DynamicDashboardProps) {
  const [rangeMinutes, setRangeMinutes]   = React.useState(1440);
  const [dateFromValue, setDateFromValue] = React.useState<Dayjs>(dayjs(subtractMinutes(rangeMinutes)));
  const [dateToValue, setDateToValue]     = React.useState<Dayjs>(dayjs(nowISOString()));
  const [widgets, setWidgets]             = React.useState<WidgetInstance[]>([]);
  const [loading, setLoading]             = React.useState(true);
  const [editMode, setEditMode]           = React.useState(false);
  const [dialogOpen, setDialogOpen]       = React.useState(false);
  const [dialogWidget, setDialogWidget]   = React.useState<Partial<WidgetInstance>>(EMPTY_WIDGET);

  React.useEffect(() => {
    setDateFromValue(dayjs(subtractMinutes(rangeMinutes)));
  }, [rangeMinutes]);

  const fetchWidgets = React.useCallback(() => {
    setLoading(true);
    getDashboardWidgets(osmName)
      .then((rows) => setWidgets(rows as WidgetInstance[]))
      .catch((err) => console.error('Failed to fetch widgets:', err))
      .finally(() => setLoading(false));
  }, [osmName]);

  React.useEffect(() => { fetchWidgets(); }, [fetchWidgets]);

  const handleAdd = () => {
    setDialogWidget({ ...EMPTY_WIDGET, osm_name: osmName, layout_order: widgets.length });
    setDialogOpen(true);
  };

  const handleEdit = (widget: WidgetInstance) => {
    setDialogWidget(widget);
    setDialogOpen(true);
  };

  const handleSaveDialog = async (w: Partial<WidgetInstance>) => {
    const payload = toDbPayload({ ...w, osm_name: osmName });
    if (w.id) {
      await updateDashboardWidget(w.id, payload);
    } else {
      await createDashboardWidget(payload);
    }
    fetchWidgets();
  };

  const handleDelete = async (id: number) => {
    await deleteDashboardWidget(id);
    setWidgets((prev) => prev.filter((w) => w.id !== id));
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= widgets.length) return;
    const updated = [...widgets];
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    const reordered = updated.map((w, i) => ({ ...w, layout_order: i }));
    setWidgets(reordered);
    await reorderDashboardWidgets(reordered.map(({ id, layout_order }) => ({ id, layout_order })));
  };

  return (
    <>
      <BaseDashboard
        dashboard_header={
            <DashboardHeader
              title={osmName}
              rangeMinutes={rangeMinutes}
              setRangeMinutes={setRangeMinutes}
              dateFromValue={dateFromValue}
              setDateFromValue={setDateFromValue}
              dateToValue={dateToValue}
              setDateToValue={setDateToValue}
            />
        }
        dashboard_content={
          <Box sx={{ gridColumn: '1 / -1', minHeight: '100vh', overflowY: 'auto' }}>
            {isAdmin && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, pt: 1 }}>
                {editMode && (
                  <Tooltip title="Add widget">
                    <IconButton onClick={handleAdd}><AddIcon /></IconButton>
                  </Tooltip>
                )}
                <Tooltip title={editMode ? 'Done editing' : 'Edit dashboard'}>
                  <IconButton onClick={() => setEditMode((p) => !p)} color={editMode ? 'primary' : 'default'}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {loading ? (
              <CircularProgress sx={{ m: 2 }} />
            ) : (
              <DashboardContent
                widgets={widgets}
                osmName={osmName}
                dateFromValue={dateFromValue}
                dateToValue={dateToValue}
                editMode={editMode}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMove={handleMove}
              />
            )}
          </Box>
        }
      />

      <WidgetDialog
        open={dialogOpen}
        initial={dialogWidget}
        osmName={osmName}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveDialog}
      />
    </>
  );
}
